import { AdvisorBubble, Column } from '@/components'
import { useSnackbar } from '@/providers'
import { useUpdateUser } from '@/services'
import { UserProfile } from '@/types/user-profile'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'
import { FormInputField, FormPhone } from '../../../../FormItem'
import { StepLayout } from '../components'
import { useChecklistStepper } from '../hooks/useChecklistStepper'
import { contactDataValidationSchema } from '../validationSchema'
import { ContactDataFormValues, StepperFormValues } from '../utils/types'

const ContactDataForm = () => {
  const { t } = useTranslation()

  return (
    <Column gap="16px">
      <Column>
        <FormInputField
          name="firstName"
          label={t(
            'riskChecklist.businessInformationFlow.contactData.firstName',
          )}
        />
        <Column gap="12px">
          <FormInputField
            name="surname"
            label={t(
              'riskChecklist.businessInformationFlow.contactData.surname',
            )}
          />
          <FormPhone name="phone" noMargin />
        </Column>
      </Column>
      <AdvisorBubble
        text={t(
          'riskChecklist.businessInformationFlow.contactData.advisorBubbleText',
        )}
      />
    </Column>
  )
}

interface OutletContext {
  user: UserProfile
}

const makeInitialValues = (
  user: UserProfile,
  formValues: StepperFormValues,
) => {
  if (formValues.firstName && formValues.surname && formValues.phone) {
    return formValues
  }

  return {
    firstName: user.name,
    surname: user.surname,
    phone: user.phone,
  }
}

export const ContactData = () => {
  const { t } = useTranslation()
  const snackbar = useSnackbar()
  const { user } = useOutletContext<OutletContext>()
  const updateUserMutation = useUpdateUser(user?.auth0)
  const { goToNext, formValues } = useChecklistStepper()

  const handleSubmit = async (values: ContactDataFormValues) => {
    try {
      await updateUserMutation.mutateAsync({
        name: values.firstName,
        surname: values.surname,
        phone: values.phone,
      })
      goToNext(values)
    } catch (_e) {
      snackbar.error(t('snackbars.error.generalMsg'))
    }
  }

  return (
    <StepLayout
      title={t('riskChecklist.businessInformationFlow.contactData.title')}
      subtitle={t('riskChecklist.businessInformationFlow.contactData.subtitle')}
      onNext={handleSubmit}
      isLoading={updateUserMutation.isLoading}
      initialValues={makeInitialValues(user, formValues)}
      validationSchema={() =>
        contactDataValidationSchema(
          t('validations', {
            returnObjects: true,
          }),
        )
      }
    >
      <ContactDataForm />
    </StepLayout>
  )
}
